/**
 * AD-021's canopy as the **player** wears it: one rig, followed down, collapsed
 * and despawned when he is on his feet.
 *
 * The silk is `canopy-kit.ts`'s and is shared with AD-021b's ambience jumpers,
 * so there is one parachute in this port and not two that resemble each other.
 * What lives here is only what is true of *his* canopy: that the camera is on
 * it, that it is hidden until he jumps, and what happens when he lands.
 *
 * ## It never used to go away, and the user asked for that
 *
 * `update` kept the group visible on `landed` for ever — *"a collapsed canopy
 * in the field is where he came down"* — and since `stepParachute` returns a
 * landed state unchanged, the fully **inflated** canopy stood in the field like
 * a tent for the rest of the sortie. The feedback was right and the forever was
 * not. Requested by the user, 2026-08-27: *"make sure the parachute properly
 * despawns upon landing"*. It now collapses over `CANOPY_COLLAPSE_SECONDS`,
 * lies as a settled heap for `CANOPY_LINGER_SECONDS`, and is removed.
 */

import * as THREE from "three/webgpu";
import type { ParachuteState } from "@red-opus/engine";
import {
  createCanopyKit,
  CANOPY_COLLAPSE_SECONDS,
  CANOPY_LINGER_SECONDS,
  PARACHUTE_GEOMETRY,
  type CanopyRig,
} from "./canopy-kit.ts";

export * from "./canopy-kit.ts";

const SWAY_RAD = PARACHUTE_GEOMETRY.SWAY_RAD;

export interface ParachuteVisual {
  readonly group: THREE.Group;
  /** Places the canopy for this frame, or hides it when nobody is under one. */
  update(state: ParachuteState | undefined, dtS: number): void;
  /** Whether the canopy is drawn at all this frame — what a test reads. */
  drawn(): boolean;
  dispose(): void;
}

/**
 * The player's own canopy: one rig, and the landing sequence the user asked
 * for.
 *
 * The kit is overkill for a single canopy and is used anyway, deliberately —
 * it is what stops the player's silk and the ambience silk becoming two
 * parachutes that agree today and drift tomorrow.
 */
class ParachuteVisualImpl implements ParachuteVisual {
  readonly group = new THREE.Group();
  private readonly kit = createCanopyKit();
  /**
   * Built at boot and hidden, never on the frame he jumps — see
   * `CanopyRig.setVisible` for what the lazy version cost.
   */
  private readonly rig: CanopyRig;
  private shown = false;
  private elapsedS = 0;
  /** Seconds since he arrived, which is what the collapse and the linger count. */
  private downS = 0;
  private disposed = false;

  constructor() {
    this.rig = this.kit.mint();
    this.group.add(this.rig.group);
    this.group.visible = false;
  }

  update(state: ParachuteState | undefined, dtS: number): void {
    if (this.disposed) return;
    // `dead` draws nothing: he reached the ground before the canopy took his
    // weight, and there is no silk to show.
    if (!state || state.status === "dead") {
      this.retire();
      return;
    }
    if (state.status === "landed") {
      this.downS += dtS;
      // He is down, the silk has collapsed and the heap has had its moment.
      if (this.downS >= CANOPY_COLLAPSE_SECONDS + CANOPY_LINGER_SECONDS) {
        this.retire();
        return;
      }
    } else {
      this.downS = 0;
    }

    const rig = this.rig;
    this.shown = true;
    // The *outer* group carries the position and the visibility, which is the
    // contract this module had before it grew a kit: the rig sits at its origin.
    this.group.visible = true;
    this.group.position.set(state.position.x, state.position.y, state.position.z);
    // Before the canopy blossoms there is no canopy: he is a falling man.
    rig.setOpen(state.status !== "falling");
    this.elapsedS += dtS;
    const landed = state.status === "landed";
    rig.setCollapse(landed ? this.downS / CANOPY_COLLAPSE_SECONDS : 0);
    rig.setSway(this.elapsedS, landed ? 0 : SWAY_RAD);
  }

  drawn(): boolean {
    return this.shown;
  }

  /**
   * Nobody under a canopy. The rig is **hidden, not freed**: it was built at
   * boot precisely so that neither showing nor hiding it costs a buffer upload.
   */
  private retire(): void {
    this.shown = false;
    this.group.visible = false;
    this.rig.reset();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.shown = false;
    this.group.visible = false;
    this.kit.dispose();
  }
}

export function createParachuteVisual(): ParachuteVisual {
  return new ParachuteVisualImpl();
}
